import React, { useState, useEffect } from 'react';
import { UserCircleIcon, KeyIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { useSelector, useDispatch } from 'react-redux';
import { selectCurrentUser, selectIsSuperAdmin, updateUserEmpresa } from '../features/auth/authSlice';
import { UserRole } from '../features/auth/types';
import { ChangePasswordForm } from '../features/users/components/ChangePasswordForm';
import { useGetEmpresasQuery } from '../features/empresas/api/empresasApiSlice';
import { showToast } from '../components/ui/Toast';

export const PerfilPage = () => {
  // Obtener el usuario actual del estado de Redux
  const currentUser = useSelector(selectCurrentUser);
  const isSuperAdmin = useSelector(selectIsSuperAdmin);
  const dispatch = useDispatch();
  
  // Estado para el formulario de información personal
  const [selectedEmpresaId, setSelectedEmpresaId] = useState<number | null>(currentUser?.empresa_id || null);
  
  // Obtener la lista de empresas solo si es superadmin
  const { data: empresasData, isLoading: isEmpresasLoading } = useGetEmpresasQuery({}, {
    skip: !isSuperAdmin
  });

  // Actualizar selectedEmpresaId cuando cambia el usuario
  useEffect(() => {
    if (currentUser?.empresa_id) {
      setSelectedEmpresaId(currentUser.empresa_id);
    }
  }, [currentUser?.empresa_id]);

  // Handler para cambiar la empresa seleccionada (solo para superadmin)
  const handleEmpresaChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const empresaId = e.target.value ? Number(e.target.value) : null;
    setSelectedEmpresaId(empresaId);
    
    // Actualizar la empresa seleccionada en el estado de Redux
    if (currentUser) {
      dispatch(updateUserEmpresa({
        ...currentUser,
        empresa_id: empresaId,
        empresa: empresaId ? empresasData?.empresas.find(e => e.id === empresaId) : null,
        role: currentUser.role as UserRole
      }));
    }
    
    // Mostrar notificación con el nuevo componente Toast
    showToast('Empresa seleccionada actualizada. Ahora puede acceder a los textos de esta empresa.', 'success');
  };

  // Función para determinar el rol en formato legible
  const getRolName = (role?: string) => {
    switch (role) {
      case 'admin':
        return 'Administrador';
      case 'superadmin':
        return 'Superadministrador';
      case 'user':
        return 'Usuario';
      default:
        return 'Usuario';
    }
  };
  
  return (
    <div>
      <h1 className="text-2xl font-bold text-foreground mb-6">Mi Perfil</h1>
      
      <div className="space-y-6">
        {/* Información del perfil */}
        <div className="bg-card text-card-foreground p-6 rounded-lg shadow-sm border border-border">
          <div className="text-lg font-medium text-foreground mb-4">
            <div className="flex items-center">
              <UserCircleIcon className="h-5 w-5 mr-2 text-muted-foreground" />
              <span>Información Personal</span>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-sm font-medium text-foreground mb-1">
                  Email
                </div>
                <input 
                  type="email"
                  disabled
                  value={currentUser?.email || ''}
                  className="bg-muted w-full px-3 py-2 border border-border rounded-md shadow-sm text-sm text-muted-foreground"
                />
                <p className="mt-1 text-xs text-muted-foreground">El email no puede ser modificado</p>
              </div>
              
              <div>
                <div className="text-sm font-medium text-foreground mb-1">
                  Rol
                </div>
                <input 
                  type="text"
                  disabled
                  value={getRolName(currentUser?.role)}
                  className="bg-muted w-full px-3 py-2 border border-border rounded-md shadow-sm text-sm text-muted-foreground"
                />
              </div>
            </div>
            
            {/* Selector de empresa (solo para superadmin) */}
            {isSuperAdmin && (
              <div className="mt-4">
                <div className="text-sm font-medium text-foreground mb-1">
                  <div className="flex items-center">
                    <BuildingOfficeIcon className="h-4 w-4 mr-1 text-muted-foreground" />
                    <span>Seleccionar Empresa</span>
                  </div>
                </div>
                <div className="relative">
                  <select
                    value={selectedEmpresaId || ''}
                    onChange={handleEmpresaChange}
                    className="w-full px-3 py-2 border border-border rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary bg-background text-foreground sm:text-sm"
                    disabled={isEmpresasLoading}
                  >
                    <option value="">-- Sin empresa seleccionada --</option>
                    {empresasData?.empresas?.map(empresa => (
                      <option key={empresa.id} value={empresa.id}>
                        {empresa.nombre}
                      </option>
                    ))}
                  </select>
                  {isEmpresasLoading && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <svg className="animate-spin h-4 w-4 text-muted-foreground" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                  )}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Como Superadmin, puede seleccionar una empresa para acceder a sus textos
                </p>
              </div>
            )}
          </div>
        </div>
        
        {/* Cambio de contraseña */}
        <div className="bg-card text-card-foreground p-6 rounded-lg shadow-sm border border-border">
          <div className="text-lg font-medium text-foreground mb-4">
            <div className="flex items-center">
              <KeyIcon className="h-5 w-5 mr-2 text-muted-foreground" />
              <span>Cambiar Contraseña</span>
            </div>
          </div>
          
          <ChangePasswordForm />
        </div>
      </div>
    </div>
  );
}; 